/**
 * EVO — «Xavfsizlik» bo'limining LUG'ATI va TALQINI.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN ALOHIDA MODUL, EKRAN ICHIDA EMAS                              ║
 * ║                                                                          ║
 * ║ Bu yerda ekran chizmaydi — bu yerda serverdan kelgan MASHINA KODI odam   ║
 * ║ o'qiydigan ma'noga aylantiriladi: `sales_orders` → «Sotuv buyurtmasi»,   ║
 * ║ `permission_denied` → «Ruxsat rad etildi», `{os,client,kind}` → «Android ║
 * ║ · Chrome · telefon».                                                     ║
 * ║                                                                          ║
 * ║ Ular ekran ichida qolsa, uchta narsa yo'qolardi:                         ║
 * ║   1. SINALADIGANLIK — 68 ta jadval nomi va 10 ta amalning to'liqligini   ║
 * ║      React komponentini render qilmasdan tekshirib bo'lmasdi;            ║
 * ║   2. UCH TIL — tarjima kalitlari ekran bo'ylab sochilib ketardi;         ║
 * ║   3. FILTR bilan JADVAL bir xil nomni ishlatishi kafolati — filtrda      ║
 * ║      «Hisob-faktura», jadvalda `invoices` chiqishi eng ko'p uchraydigan  ║
 * ║      xato.                                                               ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ SERVER SHARTNOMASI (jonli tekshirilgan, 2026-08-14)                      │
 * │                                                                          │
 * │ `GET /api/admin/audit/filters` → 68 ta `entityTable` (bazadan) va 10 ta  │
 * │ `action` (`AUDIT_ACTIONS` konstantasidan). Server FAQAT KOD qaytaradi,   │
 * │ tarjima qaytarmaydi — `admin.controller.ts#auditFilters()` ramkasida     │
 * │ sabab yozilgan: lug'at ikki joyda bo'lsa, ikkitasi bir kun zid bo'ladi.  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import type { TranslationKey } from './i18n';
import { EMPTY_VALUE, formatDate, formatDateTime, formatNumber, formatPercent, formatRate } from './format';

// ---------------------------------------------------------------------------
// Amallar
// ---------------------------------------------------------------------------

/**
 * Bazadagi `ck_audit_logs_action` CHECK cheklovidagi to'plamning AYNAN o'zi
 * (`packages/db` → `AUDIT_ACTIONS`, `admin.controller.ts` shu ro'yxat bilan
 * so'rovni tekshiradi va begona qiymatga 400 beradi).
 *
 * Nusxa ko'chirilgani — ongli: veb paketi `@evo/db` ga bog'lanmaydi (u
 * Drizzle va Postgres tiplarini brauzer bandliga tortib kelardi). Nusxa
 * eskirsa oqibati YUMSHOQ: yangi amal filtrda ko'rinmaydi, lekin jurnalda
 * ko'rinaveradi va ekran yiqilmaydi (`actionLabelKey` noma'lum kodni ham
 * qabul qiladi).
 */
export const AUDIT_ACTIONS = [
  'insert',
  'update',
  'delete',
  'override',
  'login',
  'logout',
  'login_failed',
  'permission_denied',
  'export',
  'print',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export function isAuditAction(value: unknown): value is AuditAction {
  return typeof value === 'string' && (AUDIT_ACTIONS as readonly string[]).includes(value);
}

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ IKKI GURUH — EGASINING «ARALASH RO'YXAT» QARORINING NARXINI QOPLAYDI     ║
 * ║                                                                          ║
 * ║ Egasi hamma hodisani BITTA ro'yxatda ko'rishni tanladi va bu qaror       ║
 * ║ o'zgarmaydi. Lekin uning o'lchanadigan narxi bor: bazada bugun           ║
 * ║ `login` 2 992 ta, `permission_denied` 561 ta, `delete` esa BITTA.        ║
 * ║ Ya'ni eng qimmatli yozuv eng ko'p takrorlanadigan yozuvlar orasida       ║
 * ║ yo'qoladi.                                                               ║
 * ║                                                                          ║
 * ║ Tuzilma o'zgarmaydi — KO'RINISH o'zgaradi: hujjat amallari to'liq        ║
 * ║ kontrastda (to'ldirilgan/tusli nishon), kirish-chiqish va chop etish     ║
 * ║ esa jim (xira matn + rangli nuqta). Guruh `action` dan hosil bo'ladi,    ║
 * ║ yangi maydon kerak emas.                                                 ║
 * ║                                                                          ║
 * ║ Ikkala ro'yxat birgalikda `AUDIT_ACTIONS` ni TO'LIQ qoplashi shart —     ║
 * ║ buni test tekshiradi (`__tests__/audit-log.test.ts`).                    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export const DOCUMENT_ACTIONS = ['insert', 'update', 'delete', 'override'] as const;

export const ACCESS_ACTIONS = [
  'login',
  'logout',
  'login_failed',
  'permission_denied',
  'export',
  'print',
] as const;

/** Hujjatga tegilgan amalmi? Noma'lum kod — «yo'q» (jim ko'rinishda chiziladi). */
export function isDocumentAction(action: string): boolean {
  return (DOCUMENT_ACTIONS as readonly string[]).includes(action);
}

const ACTION_LABELS: Readonly<Record<AuditAction, TranslationKey>> = {
  insert: 'audit.action.insert',
  update: 'audit.action.update',
  delete: 'audit.action.delete',
  override: 'audit.action.override',
  login: 'audit.action.login',
  logout: 'audit.action.logout',
  login_failed: 'audit.action.loginFailed',
  permission_denied: 'audit.action.permissionDenied',
  export: 'audit.action.export',
  print: 'audit.action.print',
};

/**
 * Amal kodi → tarjima kaliti. Noma'lum kod uchun `null`.
 *
 * `null` qaytishi — «ekran yiqilmasin» emas, «YOLG'ON YOZMASIN»: ilgari bu
 * yerda `?? 'audit.action.update'` turardi va u noma'lum amalni ekranda
 * «O'zgartirildi» deb ko'rsatardi. Auditda bu eng yomon xato turi —
 * yozuvning MA'NOSI o'zgarib ketadi va buni hech narsa bilan tekshirib
 * bo'lmaydi.
 */
export function actionLabelKey(action: string): TranslationKey | null {
  return isAuditAction(action) ? ACTION_LABELS[action] : null;
}

// ---------------------------------------------------------------------------
// Hujjat turlari (`entity_table`)
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ XOM JADVAL NOMI EKRANGA CHIQMAYDI                                        ║
 * ║                                                                          ║
 * ║ Ilgari filtr ERKIN MATN edi va foydalanuvchi qo'lda `invoices` deb       ║
 * ║ yozardi. Bu ikki xil zarar berardi:                                      ║
 * ║   • dasturchi tili ekranga chiqardi — bu repoda ataylab tozalangan;      ║
 * ║   • harf xatosi (`invoice`) BO'SH ro'yxat qaytarardi va uni odam         ║
 * ║     «hech narsa bo'lmagan» deb o'qirdi.                                  ║
 * ║                                                                          ║
 * ║ Endi tanlov ro'yxati serverdagi HAQIQIY qiymatlardan quriladi            ║
 * ║ (`/admin/audit/filters`), nomi esa shu jadvaldan olinadi.                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TARJIMASIZ KOD PAYDO BO'LSA NIMA BO'LADI — VA NIMA UCHUN AYNAN SHUNDAY   │
 * │                                                                          │
 * │ Ertaga yangi modul yangi jadvalga yozadi va u bu ro'yxatda BO'LMAYDI.    │
 * │ Uchta variant ko'rildi:                                                  │
 * │                                                                          │
 * │   1. «Boshqa hujjat» — RAD ETILDI. Ma'lumotni YO'QOTADI: auditor         │
 * │      qaysi hujjat turi ekanini umuman bilmay qoladi va uni boshqa        │
 * │      yo'l bilan bilib ham bo'lmaydi.                                     │
 * │   2. Xom kodni JIMGINA ko'rsatish — RAD ETILDI. Dasturchi tili           │
 * │      ekranga qaytadi va u NORMAL nomdan farq qilmaydi, ya'ni hech kim    │
 * │      tarjima yetishmayotganini payqamaydi.                               │
 * │   3. TANLANDI: xom kod KO'RSATILADI, lekin BELGI bilan — ekranda u       │
 * │      alohida ko'rinadi (`audit.tableUntranslated`), filtr panelida esa   │
 * │      ularning SONI aytiladi («3 ta hujjat turi tarjimasiz»).             │
 * │                                                                          │
 * │ KAMCHILIGI OCHIQ: kod baribir ekranda turadi va foydalanuvchi uni        │
 * │ o'qishga majbur. Bu 1-variantdan yomonroq ko'rinadi, lekin auditda       │
 * │ noaniq nom yo'qolgan nomdan afzal: «nima o'zgardi?» degan savolga        │
 * │ `qr_codes_v2` javob beradi, «Boshqa hujjat» esa yo'q.                    │
 * │                                                                          │
 * │ SEZDIRUVCHI MEXANIZM ikkita va ikkalasi ham HAQIQIY ma'lumotdan:         │
 * │   • ekranda — filtrdagi hisob (serverdan kelgan ro'yxat ustida);         │
 * │   • dasturchida — `import.meta.env.DEV` da konsol ogohlantirishi.        │
 * │ Uchinchisi — test — YANGI jadvalni ko'ra olmaydi (u bazani bilmaydi) va  │
 * │ shuning uchun faqat uch tilning MOSLIGINI tekshiradi.                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Ro'yxat 2026-08-14 da bazadan olingan: `/admin/audit/filters` 68 ta kod
 * qaytardi va shu 68 tasi shu yerda.
 */
export const AUDIT_TABLE_LABELS: Readonly<Record<string, TranslationKey>> = {
  accounting_periods: 'audit.table.accountingPeriods',
  approval_delegations: 'audit.table.approvalDelegations',
  approval_requests: 'audit.table.approvalRequests',
  approval_rules: 'audit.table.approvalRules',
  bonus_entries: 'audit.table.bonusEntries',
  branches: 'audit.table.branches',
  cash_accounts: 'audit.table.cashAccounts',
  cash_flow_items: 'audit.table.cashFlowItems',
  cash_transactions: 'audit.table.cashTransactions',
  charity_appropriations: 'audit.table.charityAppropriations',
  charity_payouts: 'audit.table.charityPayouts',
  commission_rules: 'audit.table.commissionRules',
  commission_runs: 'audit.table.commissionRuns',
  cost_types: 'audit.table.costTypes',
  credit_notes: 'audit.table.creditNotes',
  customer_contacts: 'audit.table.customerContacts',
  customer_prices: 'audit.table.customerPrices',
  customer_users: 'audit.table.customerUsers',
  customers: 'audit.table.customers',
  daily_rates: 'audit.table.dailyRates',
  day_closings: 'audit.table.dayClosings',
  dealer_rebate_tiers: 'audit.table.dealerRebateTiers',
  dealer_users: 'audit.table.dealerUsers',
  dealers: 'audit.table.dealers',
  deliveries: 'audit.table.deliveries',
  documents: 'audit.table.documents',
  employee_advances: 'audit.table.employeeAdvances',
  employee_debts: 'audit.table.employeeDebts',
  employees: 'audit.table.employees',
  expenses: 'audit.table.expenses',
  fx_revaluations: 'audit.table.fxRevaluations',
  goods_receipts: 'audit.table.goodsReceipts',
  installers: 'audit.table.installers',
  inventory_transactions: 'audit.table.inventoryTransactions',
  invoices: 'audit.table.invoices',
  journals: 'audit.table.journals',
  kpi_plans: 'audit.table.kpiPlans',
  landed_cost_runs: 'audit.table.landedCostRuns',
  payments: 'audit.table.payments',
  payroll_runs: 'audit.table.payrollRuns',
  po_payment_terms: 'audit.table.poPaymentTerms',
  price_list_items: 'audit.table.priceListItems',
  price_lists: 'audit.table.priceLists',
  print_templates: 'audit.table.printTemplates',
  product_points: 'audit.table.productPoints',
  products: 'audit.table.products',
  purchase_orders: 'audit.table.purchaseOrders',
  qr_batches: 'audit.table.qrBatches',
  qr_codes: 'audit.table.qrCodes',
  redemptions: 'audit.table.redemptions',
  reorder_run_lines: 'audit.table.reorderRunLines',
  reorder_runs: 'audit.table.reorderRuns',
  rewards: 'audit.table.rewards',
  sales_orders: 'audit.table.salesOrders',
  sales_return_lines: 'audit.table.salesReturnLines',
  sales_returns: 'audit.table.salesReturns',
  sessions: 'audit.table.sessions',
  settings: 'audit.table.settings',
  shipment_costs: 'audit.table.shipmentCosts',
  shipments: 'audit.table.shipments',
  stock_counts: 'audit.table.stockCounts',
  stock_transfers: 'audit.table.stockTransfers',
  supplier_invoices: 'audit.table.supplierInvoices',
  suppliers: 'audit.table.suppliers',
  tenants: 'audit.table.tenants',
  user_roles: 'audit.table.userRoles',
  users: 'audit.table.users',
  warehouses: 'audit.table.warehouses',
};

/** Tarjima kaliti yoki `null` — chaqiruvchi `null` da BELGI qo'yadi. */
export function tableLabelKey(code: string): TranslationKey | null {
  return AUDIT_TABLE_LABELS[code] ?? null;
}

/**
 * Serverdan kelgan ro'yxatdagi TARJIMASIZ kodlar.
 *
 * Ro'yxat SERVERNIKI, ya'ni bu haqiqiy ma'lumot: bazada uchragan, lekin bu
 * yerda nomi yo'q jadvallar. Ekran shuni sanab ko'rsatadi.
 */
export function untranslatedTables(codes: readonly string[]): readonly string[] {
  return codes.filter((code) => tableLabelKey(code) === null);
}

/**
 * Dasturchiga ogohlantirish — FAQAT `dev` da va har kod uchun BIR MARTA.
 *
 * Ishlab chiqarishda jim: konsolga har render'da yozish foydalanuvchi
 * brauzerini shovqinga to'ldiradi va hech kimga yetib bormaydi.
 */
const warned = new Set<string>();

export function warnUntranslatedTables(codes: readonly string[]): void {
  if (!import.meta.env.DEV) return;
  for (const code of codes) {
    if (warned.has(code)) continue;
    warned.add(code);
    console.warn(
      `[evo] Audit: «${code}» hujjat turining nomi yo'q. ` +
        'lib/audit-log.ts dagi AUDIT_TABLE_LABELS ga va uchala i18n fayliga qo\'shing.',
    );
  }
}

// ---------------------------------------------------------------------------
// `changedFields` — XIZMAT MAYDONLARI HUJJAT MAYDONIDAN AJRATILADI
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ `_editWindow` — «MAYDON O'ZGARDI» EMAS, «QAROR SHUNDAY QABUL QILINDI»    ║
 * ║                                                                          ║
 * ║ Server `changed_fields` ichiga xizmat belgilarini qo'shadi                ║
 * ║ (`audit.service.ts#withControlMarks`):                                    ║
 * ║                                                                          ║
 * ║   `_editWindow: { old: null, new: 'override' }`                          ║
 * ║       → tuzatish 24 soatlik oynadan CHETLAB o'tilgan                     ║
 * ║         (`correction.override` ruxsati bilan);                           ║
 * ║   `_editWindow: { old: null, new: 'within_window' }`                     ║
 * ║       → oyna TEKSHIRILDI va tuzatish uning ichida edi;                   ║
 * ║   belgi umuman yo'q                                                       ║
 * ║       → bu amalga oyna qoidasi tegishli emas.                            ║
 * ║                                                                          ║
 * ║ Ularni qolgan maydonlar bilan bir qatorda chizish YOLG'ON bo'lardi:      ║
 * ║ ekranda «_editWindow: — → override» degan qator chiqardi va uni odam     ║
 * ║ hujjatning `_editWindow` degan maydoni deb o'qirdi. `old` HAR DOIM       ║
 * ║ `null` — ya'ni «eskidan yangiga» ko'rinishining o'zi ma'nosiz.           ║
 * ║                                                                          ║
 * ║ Shuning uchun belgi ro'yxatdan CHIQARILADI va amal yonida ALOHIDA        ║
 * ║ nishon bo'lib turadi. `override` — bu bo'limning eng qimmatli yozuvi:    ║
 * ║ qoida buzilmadi, undan ATAYLAB chetlashildi va buni kimdir qilgan.       ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Ikkinchi xizmat maydoni — `event` (`businessEvent()` naqshi): biznes
 * hodisasining nomi (`payment_reversed`). U ham «eskidan yangiga» emas,
 * shuning uchun alohida chiqadi.
 *
 * ⚠️ QOIDA `_` BILAN BOSHLANISHIDA, RO'YXATDA EMAS: server izohi shuni
 * kafolatlaydi («xizmat maydoni `_` bilan boshlanadi — hujjat maydoni bilan
 * aralashmasin», `control-marks.test.ts`). Ya'ni ertaga qo'shiladigan yangi
 * `_belgi` ham hujjat maydoni sifatida ko'rsatilmaydi — u shunchaki
 * ko'rinmay qoladi va bu xavfsiz sukut holati.
 */
export type EditWindowMark = 'override' | 'within_window';

export interface FieldChange {
  readonly old?: unknown;
  readonly new?: unknown;
}

export interface SplitChanges {
  /** Tuzatish oynasi belgisi yoki `null` — bu amalga oyna tegishli emas. */
  readonly editWindow: EditWindowMark | null;
  /** Biznes hodisasi nomi (xom kod) yoki `null`. */
  readonly event: string | null;
  /** HUJJATNING haqiqiy maydonlari — xizmat belgilaridan tozalangan. */
  readonly fields: readonly (readonly [string, FieldChange])[];
}

const EDIT_WINDOW_MARKS: readonly string[] = ['override', 'within_window'];

export function splitChanges(
  changed: Readonly<Record<string, FieldChange>> | null | undefined,
): SplitChanges {
  if (!changed) return { editWindow: null, event: null, fields: [] };

  let editWindow: EditWindowMark | null = null;
  let event: string | null = null;
  const fields: (readonly [string, FieldChange])[] = [];

  for (const [name, change] of Object.entries(changed)) {
    if (name === '_editWindow') {
      const value = change.new;
      // Noma'lum belgi JIMGINA tashlanmaydi-yu, «override» ham deb
      // ko'rsatilmaydi: ikkalasi ham yolg'on bo'lardi.
      if (typeof value === 'string' && EDIT_WINDOW_MARKS.includes(value)) {
        editWindow = value as EditWindowMark;
      }
      continue;
    }
    // Boshqa BARCHA `_…` belgilari — kelajakdagi xizmat maydonlari.
    if (name.startsWith('_')) continue;

    if (name === 'event') {
      if (typeof change.new === 'string' && change.new !== '') event = change.new;
      continue;
    }

    fields.push([name, change] as const);
  }

  return { editWindow, event, fields };
}

// ---------------------------------------------------------------------------
// Maydon nomlari
// ---------------------------------------------------------------------------

/**
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN RO'YXAT TO'LIQ EMAS — VA BU ONGLI                             │
 * │                                                                          │
 * │ Bazada bugun 188 xil maydon nomi uchraydi (o'lchangan: barcha hujjat     │
 * │ amallari bo'yicha 500 tadan yozuv). Ularning hammasini uch tilga         │
 * │ ko'chirish 564 ta satr bo'lardi va ularning ko'pchiligi bir marta ham    │
 * │ ekranga chiqmaydi.                                                       │
 * │                                                                          │
 * │ Shuning uchun bu yerda ENG KO'P uchraydiganlari turadi. Qolganlari       │
 * │ `humanizeIdentifier()` orqali o'qiladigan ko'rinishga keltiriladi:       │
 * │ `unitCost` → «Unit cost», `payment_method` → «Payment method».           │
 * │                                                                          │
 * │ KAMCHILIGI OCHIQ: bu — inglizcha o'qilgan MAYDON NOMI, tarjima emas.     │
 * │ Jadval nomidan farqli, bu yerda «Boshqa maydon» varianti umuman          │
 * │ ko'rilmadi: maydon nomi o'zgarishning YAGONA belgisi va uni yashirish    │
 * │ satrni ma'nosiz qilardi.                                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const AUDIT_FIELD_LABELS: Readonly<Record<string, TranslationKey>> = {
  status: 'audit.field.status',
  amount: 'audit.field.amount',
  amountBase: 'audit.field.amountBase',
  totalAmount: 'audit.field.totalAmount',
  totalBase: 'audit.field.totalBase',
  balanceBase: 'audit.field.balanceBase',
  balanceAmount: 'audit.field.balanceAmount',
  vatAmount: 'audit.field.vatAmount',
  vatBase: 'audit.field.vatBase',
  vatRate: 'audit.field.vatRate',
  listAmount: 'audit.field.listAmount',
  discountAmount: 'audit.field.discountAmount',
  priceAmount: 'audit.field.priceAmount',
  unitCost: 'audit.field.unitCost',
  quantity: 'audit.field.quantity',
  currency: 'audit.field.currency',
  currencyCode: 'audit.field.currencyCode',
  rate: 'audit.field.rate',
  fxRate: 'audit.field.fxRate',
  fxRateManual: 'audit.field.fxRateManual',
  rateDate: 'audit.field.rateDate',
  docNo: 'audit.field.docNo',
  invoiceNo: 'audit.field.invoiceNo',
  deliveryNo: 'audit.field.deliveryNo',
  batchNo: 'audit.field.batchNo',
  lineCount: 'audit.field.lineCount',
  codeCount: 'audit.field.codeCount',
  format: 'audit.field.format',
  mode: 'audit.field.mode',
  method: 'audit.field.method',
  paymentMethod: 'audit.field.paymentMethod',
  paymentTermDays: 'audit.field.paymentTermDays',
  dueDate: 'audit.field.dueDate',
  invoiceDate: 'audit.field.invoiceDate',
  orderDate: 'audit.field.orderDate',
  receiptDate: 'audit.field.receiptDate',
  incurredDate: 'audit.field.incurredDate',
  businessDate: 'audit.field.businessDate',
  validFrom: 'audit.field.validFrom',
  postedAt: 'audit.field.postedAt',
  occurredAt: 'audit.field.occurredAt',
  name: 'audit.field.name',
  nameUz: 'audit.field.nameUz',
  nameRu: 'audit.field.nameRu',
  fullName: 'audit.field.fullName',
  legalName: 'audit.field.legalName',
  code: 'audit.field.code',
  sku: 'audit.field.sku',
  phone: 'audit.field.phone',
  address: 'audit.field.address',
  notes: 'audit.field.notes',
  description: 'audit.field.description',
  reason: 'audit.field.reason',
  reasonCode: 'audit.field.reasonCode',
  reversalReason: 'audit.field.reversalReason',
  reversalDocNo: 'audit.field.reversalDocNo',
  writeoffReason: 'audit.field.writeoffReason',
  overrideReason: 'audit.field.overrideReason',
  differenceReason: 'audit.field.differenceReason',
  isActive: 'audit.field.isActive',
  isBlocked: 'audit.field.isBlocked',
  isDefault: 'audit.field.isDefault',
  isSellable: 'audit.field.isSellable',
  isPurchasable: 'audit.field.isPurchasable',
  creditLimitAmount: 'audit.field.creditLimitAmount',
  creditLimitBase: 'audit.field.creditLimitBase',
  creditLimitCurrency: 'audit.field.creditLimitCurrency',
  outstandingBase: 'audit.field.outstandingBase',
  creditCheckResult: 'audit.field.creditCheckResult',
  creditOverrideBy: 'audit.field.creditOverrideBy',
  privilegeEscalationDenied: 'audit.field.privilegeEscalationDenied',
  sessionsRevoked: 'audit.field.sessionsRevoked',
  role: 'audit.field.role',
  locale: 'audit.field.locale',
  document: 'audit.field.document',
  missingFields: 'audit.field.missingFields',
  salaryAmount: 'audit.field.salaryAmount',
  position: 'audit.field.position',
  employeeNo: 'audit.field.employeeNo',
  hiredAt: 'audit.field.hiredAt',
  firedAt: 'audit.field.firedAt',
  containerNo: 'audit.field.containerNo',
  vehicleNo: 'audit.field.vehicleNo',
  driverName: 'audit.field.driverName',
  eta: 'audit.field.eta',
  etd: 'audit.field.etd',
  ata: 'audit.field.ata',
  weightKg: 'audit.field.weightKg',
  volumeM3: 'audit.field.volumeM3',
  cashBalance: 'audit.field.cashBalance',
  cashIn: 'audit.field.cashIn',
  cashOut: 'audit.field.cashOut',
  countedAmount: 'audit.field.countedAmount',
  openingBalance: 'audit.field.openingBalance',
  expectedBalance: 'audit.field.expectedBalance',
  difference: 'audit.field.difference',
  inventoryValue: 'audit.field.inventoryValue',
  txnType: 'audit.field.txnType',
  accountType: 'audit.field.accountType',
  customerType: 'audit.field.customerType',
  orderSource: 'audit.field.orderSource',
  source: 'audit.field.source',
};

export function fieldLabelKey(field: string): TranslationKey | null {
  return AUDIT_FIELD_LABELS[field] ?? null;
}

/**
 * `unitCost` → «Unit cost», `payment_method` → «Payment method».
 *
 * Ko'p sonli qisqartmalar (`id`, `sku`, `vat`) ATAYLAB alohida ishlanmaydi:
 * har istisno keyingi istisnoni chaqiradi va natijada bu yerda ikkinchi,
 * yarim to'ldirilgan lug'at paydo bo'lardi. Kerakli nom yuqoridagi
 * ro'yxatga yoziladi — bitta yo'l.
 */
export function humanizeIdentifier(raw: string): string {
  const words = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_+/g, ' ')
    .trim()
    .toLowerCase();
  return words === '' ? raw : words.charAt(0).toUpperCase() + words.slice(1);
}

// ---------------------------------------------------------------------------
// Qiymatlar
// ---------------------------------------------------------------------------

/** Pul maydonlari — `formatNumber(…, 2)` bilan chiziladi. */
const MONEY_FIELDS: ReadonlySet<string> = new Set([
  'amount', 'amountBase', 'totalAmount', 'totalBase', 'balanceBase', 'balanceAmount',
  'balanceAfter', 'balanceBefore', 'vatAmount', 'vatBase', 'listAmount', 'discountAmount',
  'priceAmount', 'unitCost', 'totalCostBase', 'inventoryValue', 'cashBalance', 'cashIn',
  'cashOut', 'countedAmount', 'openingBalance', 'expectedBalance', 'difference', 'paidBase',
  'netBase', 'netResult', 'incomeTotal', 'expenseTotal', 'purchaseBase', 'cogsVarianceBase',
  'appliedAmount', 'fromAmountBase', 'creditLimitAmount', 'creditLimitBase', 'outstandingBase',
  'salaryAmount',
]);

const RATE_FIELDS: ReadonlySet<string> = new Set(['rate', 'fxRate', 'fxRateManual', 'purchaseRate']);

const PERCENT_FIELDS: ReadonlySet<string> = new Set(['vatRate', 'defaultDiscountPct', 'rebatePct']);

/** Butun son bo'lib ko'rsatiladigan hisoblar. */
const COUNT_FIELDS: ReadonlySet<string> = new Set(['lineCount', 'codeCount', 'suggestedCount', 'purchaseOrderCount']);

/** `"1200000.0000"` ko'rinishidagi XOM son — `parseFloat` QILINMAYDI (`format.ts` qoidasi). */
const NUMERIC_STRING = /^-?\d+(\.\d+)?$/;
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/**
 * Qiymatni ko'rsatish uchun matnga aylantiradi.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ VALYUTA BELGISI QO'YILMAYDI — VA BU ATAYLAB                              │
 * │                                                                          │
 * │ `changedFields` da valyuta AYRIM yozuvlarda bor (`currencyCode`),        │
 * │ ko'pchiligida esa YO'Q. Ya'ni `formatMoney(value, 'UZS')` yozish —       │
 * │ TAXMIN, va u eng yomon joyda yolg'on gapirardi: dollarlik to'lov         │
 * │ ekranda «so'm» bo'lib ko'rinardi.                                        │
 * │                                                                          │
 * │ Shuning uchun `formatNumber`: ming ajratgich va ikki xona bor            │
 * │ («1 200 000.00 → 450 000.00»), valyuta esa O'Z satrida ko'rinadi —       │
 * │ `currencyCode` ham shu ro'yxatdagi maydon.                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Sana TURI emas, QIYMATI bo'yicha aniqlanadi: maydon nomiga ishonish
 * (`…Date`, `…At`) bitta jadvalda ishlab, boshqasida ishlamasdi.
 */
export function formatAuditValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number') return formatNumber(String(value), Number.isInteger(value) ? 0 : 2);

  if (typeof value === 'string') {
    if (value === '') return EMPTY_VALUE;
    if (ISO_DATE_ONLY.test(value)) return formatDate(value);
    if (ISO_TIMESTAMP.test(value)) return formatDateTime(value);

    if (NUMERIC_STRING.test(value)) {
      if (MONEY_FIELDS.has(field)) return formatNumber(value, 2);
      if (RATE_FIELDS.has(field)) return formatRate(value);
      if (PERCENT_FIELDS.has(field)) return formatPercent(value);
      if (COUNT_FIELDS.has(field)) return formatNumber(value, 0);
    }
    return value;
  }

  // Obyekt va massiv — xom JSON. Ular kamdan-kam uchraydi (`restoredDocuments`,
  // `partnerView`, `modules`) va ularni «chiroyli» qilishga urinish har turi
  // uchun alohida qoida talab qilardi.
  return JSON.stringify(value);
}

// ---------------------------------------------------------------------------
// Qurilma
// ---------------------------------------------------------------------------

export type DeviceKind = 'desktop' | 'mobile' | 'tablet' | 'bot' | 'unknown';

export interface AuditDevice {
  readonly os: string | null;
  readonly client: string | null;
  readonly kind: DeviceKind;
}

const DEVICE_KIND_LABELS: Readonly<Record<DeviceKind, TranslationKey>> = {
  desktop: 'audit.device.desktop',
  mobile: 'audit.device.mobile',
  tablet: 'audit.device.tablet',
  bot: 'audit.device.bot',
  unknown: 'audit.device.unknownKind',
};

export function deviceKindLabelKey(kind: string): TranslationKey {
  return DEVICE_KIND_LABELS[kind as DeviceKind] ?? DEVICE_KIND_LABELS.unknown;
}

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ «QAYD ETILMAGAN» VA «TANILMADI» — IKKI XIL JAVOB                         ║
 * ║                                                                          ║
 * ║ Server ikki xil `null` beradi va ular BOSHQA narsani anglatadi:          ║
 * ║                                                                          ║
 * ║   `device: null`                     → `User-Agent` UMUMAN yozilmagan.   ║
 * ║       Migratsiya 0051 dan OLDINGI yozuvlarda shunday. Jonli o'lchandi:   ║
 * ║       oxirgi 500 yozuvdan 349 tasi shu holatda.                          ║
 * ║   `device: { os: null, client: null }` → satr BOR, lekin naqshlarga mos  ║
 * ║       kelmadi. O'sha 500 dan 142 tasi (asosan `curl` va noma'lum UA).    ║
 * ║                                                                          ║
 * ║ Bo'sh katak IKKALASINI ham bir xil ko'rsatardi va tekshiruv paytida bu   ║
 * ║ hal qiluvchi farq: birinchisida DALIL YO'Q, ikkinchisida dalil bor-u     ║
 * ║ biz uni o'qiy olmadik (xom `User-Agent` esa satr ostida turadi).         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * `os` yoki `client` dan faqat bittasi tanilgan holat ham bor
 * (`{os: null, client: 'curl', kind: 'bot'}`) — o'shanda bittasi ko'rsatiladi
 * va sinf yonida turadi: «curl · bot».
 */
export function deviceParts(device: AuditDevice | null | undefined): readonly string[] {
  if (!device) return [];
  const parts: string[] = [];
  if (device.os !== null && device.os !== '') parts.push(device.os);
  if (device.client !== null && device.client !== '') parts.push(device.client);
  return parts;
}
